/**
 * Dashboard color themes.
 *
 * ── How to switch ──────────────────────────────────────────────────────────────
 * Change ACTIVE_THEME at the bottom of this file.  That's it.
 *
 * ── How to add a theme ─────────────────────────────────────────────────────────
 * Copy any existing entry, change the values, give it a key.
 *
 * ── Color format ───────────────────────────────────────────────────────────────
 * • "ds" (design-system) values are **space-separated RGB** so that Tailwind's
 *   alpha modifier syntax works:  bg-primary/30  →  rgb(R G B / 0.3)
 * • "chrome" values are regular CSS colors (hex, rgba, etc.) used via inline
 *   styles or CSS-variable arbitrary values in class names.
 */

const THEMES = {

  /* ═══════════════════════════════════════════════════════════════════════════
     EMERALD  –  the original green theme
     ═══════════════════════════════════════════════════════════════════════════ */
  emerald: {
    name: 'Emerald',
    ds: {
      '--cm-primary':                   '0 105 71',
      '--cm-primary-container':         '105 246 184',
      '--cm-primary-fixed':             '105 246 184',
      '--cm-primary-dim':               '0 92 61',
      '--cm-on-primary':                '200 255 224',
      '--cm-on-primary-container':      '0 90 60',
      '--cm-secondary':                 '0 87 189',
      '--cm-secondary-container':       '191 209 255',
      '--cm-on-secondary-container':    '0 68 150',
      '--cm-surface':                   '216 255 240',
      '--cm-surface-dim':               '143 229 201',
      '--cm-surface-bright':            '216 255 240',
      '--cm-surface-container':         '178 246 222',
      '--cm-surface-container-low':     '191 254 231',
      '--cm-surface-container-high':    '167 241 216',
      '--cm-surface-container-highest': '157 236 210',
      '--cm-surface-container-lowest':  '255 255 255',
      '--cm-surface-variant':           '157 236 210',
      '--cm-on-surface':                '0 54 42',
      '--cm-on-surface-variant':        '47 101 85',
      '--cm-on-background':             '0 54 42',
      '--cm-background':                '216 255 240',
      '--cm-outline':                   '76 129 112',
      '--cm-outline-variant':           '129 184 165',
    },
    chrome: {
      '--chrome-bg':             '#d8fff0',
      '--chrome-text':           '#00362a',
      '--chrome-sidebar-bg':     '#ecfdf5',
      '--chrome-right-sidebar':  'rgba(191,254,231,0.3)',
      '--chrome-strong':         '#064e3b',
      '--chrome-muted':          'rgba(4,120,87,0.7)',
      '--chrome-label':          'rgba(6,95,70,0.6)',
      '--chrome-faint':          'rgba(4,120,87,0.6)',
      '--chrome-body':           'rgba(6,78,59,0.8)',
      '--chrome-active-bg':      'rgba(209,250,229,0.5)',
      '--chrome-active-border':  '#059669',
      '--chrome-hover-bg':       '#d1fae5',
      '--chrome-border-subtle':  'rgba(6,78,59,0.05)',
      '--chrome-border-divider': 'rgba(6,78,59,0.1)',
      '--chrome-avatar':         '#059669',
      '--chrome-avatar-accent':  '#10b981',
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════════
     EMERALD LIGHT  –  green accents on neutral white/cream surfaces
     Same emerald identity but backgrounds are clean white/gray instead of green.
     ═══════════════════════════════════════════════════════════════════════════ */
  'emerald-light': {
    name: 'Emerald Light',
    ds: {
      '--cm-primary':                   '5 150 105',       // emerald-600 – main accent
      '--cm-primary-container':         '16 185 129',      // emerald-500 – gradient endpoint
      '--cm-primary-fixed':             '209 250 229',     // emerald-100 – subtle tints
      '--cm-primary-dim':               '4 120 87',        // emerald-700
      '--cm-on-primary':                '255 255 255',
      '--cm-on-primary-container':      '6 78 59',         // emerald-900
      '--cm-secondary':                 '14 116 144',      // cyan-700
      '--cm-secondary-container':       '207 250 254',     // cyan-100
      '--cm-on-secondary-container':    '21 94 117',       // cyan-800
      '--cm-surface':                   '255 255 255',     // white
      '--cm-surface-dim':               '241 245 249',     // slate-100
      '--cm-surface-bright':            '255 255 255',
      '--cm-surface-container':         '248 250 252',     // slate-50 – cards
      '--cm-surface-container-low':     '255 255 255',
      '--cm-surface-container-high':    '241 245 249',     // slate-100
      '--cm-surface-container-highest': '248 250 252',     // search bar
      '--cm-surface-container-lowest':  '255 255 255',
      '--cm-surface-variant':           '241 245 249',     // slate-100
      '--cm-on-surface':                '15 23 42',        // slate-900
      '--cm-on-surface-variant':        '71 85 105',       // slate-500
      '--cm-on-background':             '15 23 42',        // slate-900
      '--cm-background':                '249 250 251',     // gray-50 – page bg
      '--cm-outline':                   '203 213 225',     // slate-300
      '--cm-outline-variant':           '226 232 240',     // slate-200
    },
    chrome: {
      '--chrome-bg':             '#f9fafb',                // gray-50
      '--chrome-text':           '#0f172a',                // slate-900
      '--chrome-sidebar-bg':     '#ffffff',
      '--chrome-right-sidebar':  'rgba(248,250,252,0.8)',
      '--chrome-strong':         '#064e3b',                // emerald-900
      '--chrome-muted':          'rgba(71,85,105,0.7)',    // slate-500
      '--chrome-label':          'rgba(71,85,105,0.6)',
      '--chrome-faint':          'rgba(100,116,139,0.5)',
      '--chrome-body':           'rgba(15,23,42,0.8)',
      '--chrome-active-bg':      'rgba(209,250,229,0.5)', // emerald tint on active
      '--chrome-active-border':  '#059669',                // emerald-600
      '--chrome-hover-bg':       '#f1f5f9',                // slate-100
      '--chrome-border-subtle':  'rgba(0,0,0,0.04)',
      '--chrome-border-divider': 'rgba(0,0,0,0.08)',
      '--chrome-avatar':         '#059669',
      '--chrome-avatar-accent':  '#10b981',
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════════
     EMERALD CARDS  –  emerald-light but with green-tinted cards
     Neutral page background, but product cards have a soft emerald surface.
     ═══════════════════════════════════════════════════════════════════════════ */
  'emerald-cards': {
    name: 'Emerald Cards',
    ds: {
      '--cm-primary':                   '5 150 105',       // emerald-600
      '--cm-primary-container':         '16 185 129',      // emerald-500
      '--cm-primary-fixed':             '209 250 229',     // emerald-100
      '--cm-primary-dim':               '4 120 87',        // emerald-700
      '--cm-on-primary':                '255 255 255',
      '--cm-on-primary-container':      '6 78 59',         // emerald-900
      '--cm-secondary':                 '14 116 144',      // cyan-700
      '--cm-secondary-container':       '207 250 254',     // cyan-100
      '--cm-on-secondary-container':    '21 94 117',       // cyan-800
      '--cm-surface':                   '255 255 255',
      '--cm-surface-dim':               '241 245 249',
      '--cm-surface-bright':            '255 255 255',
      '--cm-surface-container':         '178 246 222',     // same green as emerald theme
      '--cm-surface-container-low':     '191 254 231',
      '--cm-surface-container-high':    '167 241 216',
      '--cm-surface-container-highest': '157 236 210',
      '--cm-surface-container-lowest':  '255 255 255',
      '--cm-surface-variant':           '241 245 249',
      '--cm-on-surface':                '15 23 42',
      '--cm-on-surface-variant':        '71 85 105',
      '--cm-on-background':             '15 23 42',
      '--cm-background':                '249 250 251',     // neutral page bg
      '--cm-outline':                   '203 213 225',
      '--cm-outline-variant':           '226 232 240',
    },
    chrome: {
      '--chrome-bg':             '#f9fafb',
      '--chrome-text':           '#0f172a',
      '--chrome-sidebar-bg':     '#ffffff',
      '--chrome-right-sidebar':  'rgba(248,250,252,0.8)',
      '--chrome-strong':         '#064e3b',
      '--chrome-muted':          'rgba(71,85,105,0.7)',
      '--chrome-label':          'rgba(71,85,105,0.6)',
      '--chrome-faint':          'rgba(100,116,139,0.5)',
      '--chrome-body':           'rgba(15,23,42,0.8)',
      '--chrome-active-bg':      'rgba(209,250,229,0.5)',
      '--chrome-active-border':  '#059669',
      '--chrome-hover-bg':       '#f1f5f9',
      '--chrome-border-subtle':  'rgba(0,0,0,0.04)',
      '--chrome-border-divider': 'rgba(0,0,0,0.08)',
      '--chrome-avatar':         '#059669',
      '--chrome-avatar-accent':  '#10b981',
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════════
     OCEAN  –  blue accent on neutral surfaces (calm, professional)
     Based on emerald-light structure, swaps green for blue.
     ═══════════════════════════════════════════════════════════════════════════ */
  ocean: {
    name: 'Ocean',
    ds: {
      '--cm-primary':                   '37 99 235',       // blue-600
      '--cm-primary-container':         '59 130 246',      // blue-500
      '--cm-primary-fixed':             '219 234 254',     // blue-100
      '--cm-primary-dim':               '29 78 216',       // blue-700
      '--cm-on-primary':                '255 255 255',
      '--cm-on-primary-container':      '30 64 175',       // blue-800
      '--cm-secondary':                 '5 150 105',       // emerald-600
      '--cm-secondary-container':       '209 250 229',     // emerald-100
      '--cm-on-secondary-container':    '6 78 59',         // emerald-900
      '--cm-surface':                   '255 255 255',
      '--cm-surface-dim':               '241 245 249',
      '--cm-surface-bright':            '255 255 255',
      '--cm-surface-container':         '248 250 252',
      '--cm-surface-container-low':     '255 255 255',
      '--cm-surface-container-high':    '241 245 249',
      '--cm-surface-container-highest': '248 250 252',
      '--cm-surface-container-lowest':  '255 255 255',
      '--cm-surface-variant':           '241 245 249',
      '--cm-on-surface':                '15 23 42',
      '--cm-on-surface-variant':        '71 85 105',
      '--cm-on-background':             '15 23 42',
      '--cm-background':                '249 250 251',
      '--cm-outline':                   '203 213 225',
      '--cm-outline-variant':           '226 232 240',
    },
    chrome: {
      '--chrome-bg':             '#f9fafb',
      '--chrome-text':           '#0f172a',
      '--chrome-sidebar-bg':     '#ffffff',
      '--chrome-right-sidebar':  'rgba(248,250,252,0.8)',
      '--chrome-strong':         '#1e3a8a',                // blue-900
      '--chrome-muted':          'rgba(71,85,105,0.7)',
      '--chrome-label':          'rgba(71,85,105,0.6)',
      '--chrome-faint':          'rgba(100,116,139,0.5)',
      '--chrome-body':           'rgba(15,23,42,0.8)',
      '--chrome-active-bg':      'rgba(219,234,254,0.5)', // blue tint
      '--chrome-active-border':  '#2563eb',
      '--chrome-hover-bg':       '#f1f5f9',
      '--chrome-border-subtle':  'rgba(0,0,0,0.04)',
      '--chrome-border-divider': 'rgba(0,0,0,0.08)',
      '--chrome-avatar':         '#2563eb',
      '--chrome-avatar-accent':  '#3b82f6',
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════════
     VIOLET  –  purple accent on neutral surfaces (creative, modern)
     ═══════════════════════════════════════════════════════════════════════════ */
  violet: {
    name: 'Violet',
    ds: {
      '--cm-primary':                   '124 58 237',      // violet-600
      '--cm-primary-container':         '139 92 246',      // violet-500
      '--cm-primary-fixed':             '237 233 254',     // violet-100
      '--cm-primary-dim':               '109 40 217',      // violet-700
      '--cm-on-primary':                '255 255 255',
      '--cm-on-primary-container':      '76 29 149',       // violet-800
      '--cm-secondary':                 '236 72 153',      // pink-500
      '--cm-secondary-container':       '252 231 243',     // pink-100
      '--cm-on-secondary-container':    '157 23 77',       // pink-800
      '--cm-surface':                   '255 255 255',
      '--cm-surface-dim':               '241 245 249',
      '--cm-surface-bright':            '255 255 255',
      '--cm-surface-container':         '248 250 252',
      '--cm-surface-container-low':     '255 255 255',
      '--cm-surface-container-high':    '241 245 249',
      '--cm-surface-container-highest': '248 250 252',
      '--cm-surface-container-lowest':  '255 255 255',
      '--cm-surface-variant':           '241 245 249',
      '--cm-on-surface':                '15 23 42',
      '--cm-on-surface-variant':        '71 85 105',
      '--cm-on-background':             '15 23 42',
      '--cm-background':                '249 250 251',
      '--cm-outline':                   '203 213 225',
      '--cm-outline-variant':           '226 232 240',
    },
    chrome: {
      '--chrome-bg':             '#f9fafb',
      '--chrome-text':           '#0f172a',
      '--chrome-sidebar-bg':     '#ffffff',
      '--chrome-right-sidebar':  'rgba(248,250,252,0.8)',
      '--chrome-strong':         '#4c1d95',                // violet-900
      '--chrome-muted':          'rgba(71,85,105,0.7)',
      '--chrome-label':          'rgba(71,85,105,0.6)',
      '--chrome-faint':          'rgba(100,116,139,0.5)',
      '--chrome-body':           'rgba(15,23,42,0.8)',
      '--chrome-active-bg':      'rgba(237,233,254,0.5)', // violet tint
      '--chrome-active-border':  '#7c3aed',
      '--chrome-hover-bg':       '#f1f5f9',
      '--chrome-border-subtle':  'rgba(0,0,0,0.04)',
      '--chrome-border-divider': 'rgba(0,0,0,0.08)',
      '--chrome-avatar':         '#7c3aed',
      '--chrome-avatar-accent':  '#8b5cf6',
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════════
     WARM  –  amber/orange accent on warm cream surfaces
     Cards have a subtle shadow to lift them off the background.
     ═══════════════════════════════════════════════════════════════════════════ */
  warm: {
    name: 'Warm',
    cardShadow: true,
    ds: {
      '--cm-primary':                   '217 119 6',       // amber-600
      '--cm-primary-container':         '245 158 11',      // amber-500
      '--cm-primary-fixed':             '254 243 199',     // amber-100
      '--cm-primary-dim':               '180 83 9',        // amber-700
      '--cm-on-primary':                '255 255 255',
      '--cm-on-primary-container':      '120 53 15',       // amber-800
      '--cm-secondary':                 '5 150 105',       // emerald-600
      '--cm-secondary-container':       '209 250 229',     // emerald-100
      '--cm-on-secondary-container':    '6 78 59',         // emerald-900
      '--cm-surface':                   '255 255 255',
      '--cm-surface-dim':               '245 243 240',     // warm gray
      '--cm-surface-bright':            '255 255 255',
      '--cm-surface-container':         '255 255 255',     // white cards (shadow separates)
      '--cm-surface-container-low':     '255 255 255',
      '--cm-surface-container-high':    '245 243 240',
      '--cm-surface-container-highest': '250 249 247',
      '--cm-surface-container-lowest':  '255 255 255',
      '--cm-surface-variant':           '245 243 240',
      '--cm-on-surface':                '28 25 23',        // stone-900
      '--cm-on-surface-variant':        '87 83 78',        // stone-500
      '--cm-on-background':             '28 25 23',
      '--cm-background':                '250 248 245',     // warm off-white
      '--cm-outline':                   '214 211 209',     // stone-300
      '--cm-outline-variant':           '231 229 228',     // stone-200
    },
    chrome: {
      '--chrome-bg':             '#faf8f5',                // warm off-white
      '--chrome-text':           '#1c1917',
      '--chrome-sidebar-bg':     '#ffffff',
      '--chrome-right-sidebar':  'rgba(255,255,255,0.8)',
      '--chrome-strong':         '#78350f',                // amber-900
      '--chrome-muted':          'rgba(87,83,78,0.7)',
      '--chrome-label':          'rgba(87,83,78,0.6)',
      '--chrome-faint':          'rgba(120,113,108,0.5)',
      '--chrome-body':           'rgba(28,25,23,0.8)',
      '--chrome-active-bg':      'rgba(254,243,199,0.5)', // amber tint
      '--chrome-active-border':  '#d97706',
      '--chrome-hover-bg':       '#f5f3f0',
      '--chrome-border-subtle':  'rgba(0,0,0,0.04)',
      '--chrome-border-divider': 'rgba(0,0,0,0.08)',
      '--chrome-avatar':         '#d97706',
      '--chrome-avatar-accent':  '#f59e0b',
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════════
     SLATE  –  clean white/gray with blue accent  (Notion / Linear style)
     ═══════════════════════════════════════════════════════════════════════════ */
  slate: {
    name: 'Slate',
    ds: {
      '--cm-primary':                   '59 130 246',
      '--cm-primary-container':         '219 234 254',
      '--cm-primary-fixed':             '219 234 254',
      '--cm-primary-dim':               '37 99 235',
      '--cm-on-primary':                '255 255 255',
      '--cm-on-primary-container':      '30 64 175',
      '--cm-secondary':                 '139 92 246',
      '--cm-secondary-container':       '237 233 254',
      '--cm-on-secondary-container':    '109 40 217',
      '--cm-surface':                   '248 250 252',
      '--cm-surface-dim':               '226 232 240',
      '--cm-surface-bright':            '248 250 252',
      '--cm-surface-container':         '241 245 249',
      '--cm-surface-container-low':     '248 250 252',
      '--cm-surface-container-high':    '226 232 240',
      '--cm-surface-container-highest': '203 213 225',
      '--cm-surface-container-lowest':  '255 255 255',
      '--cm-surface-variant':           '226 232 240',
      '--cm-on-surface':                '15 23 42',
      '--cm-on-surface-variant':        '71 85 105',
      '--cm-on-background':             '15 23 42',
      '--cm-background':                '248 250 252',
      '--cm-outline':                   '148 163 184',
      '--cm-outline-variant':           '203 213 225',
    },
    chrome: {
      '--chrome-bg':             '#f8fafc',
      '--chrome-text':           '#0f172a',
      '--chrome-sidebar-bg':     '#ffffff',
      '--chrome-right-sidebar':  'rgba(241,245,249,0.5)',
      '--chrome-strong':         '#0f172a',
      '--chrome-muted':          'rgba(71,85,105,0.7)',
      '--chrome-label':          'rgba(51,65,85,0.6)',
      '--chrome-faint':          'rgba(71,85,105,0.6)',
      '--chrome-body':           'rgba(15,23,42,0.8)',
      '--chrome-active-bg':      'rgba(219,234,254,0.5)',
      '--chrome-active-border':  '#3b82f6',
      '--chrome-hover-bg':       '#f1f5f9',
      '--chrome-border-subtle':  'rgba(15,23,42,0.05)',
      '--chrome-border-divider': 'rgba(15,23,42,0.08)',
      '--chrome-avatar':         '#3b82f6',
      '--chrome-avatar-accent':  '#3b82f6',
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════════
     ROSE  –  warm blush tones with rose accent  (friendly / modern)
     ═══════════════════════════════════════════════════════════════════════════ */
  rose: {
    name: 'Rose',
    ds: {
      '--cm-primary':                   '225 29 72',
      '--cm-primary-container':         '255 228 230',
      '--cm-primary-fixed':             '255 228 230',
      '--cm-primary-dim':               '190 18 60',
      '--cm-on-primary':                '255 255 255',
      '--cm-on-primary-container':      '159 18 57',
      '--cm-secondary':                 '13 148 136',
      '--cm-secondary-container':       '204 251 241',
      '--cm-on-secondary-container':    '15 118 110',
      '--cm-surface':                   '255 241 242',
      '--cm-surface-dim':               '254 205 211',
      '--cm-surface-bright':            '255 241 242',
      '--cm-surface-container':         '255 228 230',
      '--cm-surface-container-low':     '255 241 242',
      '--cm-surface-container-high':    '254 205 211',
      '--cm-surface-container-highest': '253 164 175',
      '--cm-surface-container-lowest':  '255 255 255',
      '--cm-surface-variant':           '254 205 211',
      '--cm-on-surface':                '76 5 25',
      '--cm-on-surface-variant':        '136 19 55',
      '--cm-on-background':             '76 5 25',
      '--cm-background':                '255 241 242',
      '--cm-outline':                   '251 113 133',
      '--cm-outline-variant':           '253 164 175',
    },
    chrome: {
      '--chrome-bg':             '#fff1f2',
      '--chrome-text':           '#4c0519',
      '--chrome-sidebar-bg':     '#ffffff',
      '--chrome-right-sidebar':  'rgba(255,228,230,0.4)',
      '--chrome-strong':         '#4c0519',
      '--chrome-muted':          'rgba(159,18,57,0.6)',
      '--chrome-label':          'rgba(136,19,55,0.5)',
      '--chrome-faint':          'rgba(159,18,57,0.5)',
      '--chrome-body':           'rgba(76,5,25,0.8)',
      '--chrome-active-bg':      'rgba(255,228,230,0.6)',
      '--chrome-active-border':  '#e11d48',
      '--chrome-hover-bg':       '#ffe4e6',
      '--chrome-border-subtle':  'rgba(76,5,25,0.05)',
      '--chrome-border-divider': 'rgba(76,5,25,0.1)',
      '--chrome-avatar':         '#e11d48',
      '--chrome-avatar-accent':  '#fb7185',
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════════
     SAND  –  warm stone/beige with amber accent  (earthy / calm)
     ═══════════════════════════════════════════════════════════════════════════ */
  sand: {
    name: 'Sand',
    ds: {
      '--cm-primary':                   '180 83 9',
      '--cm-primary-container':         '254 243 199',
      '--cm-primary-fixed':             '254 243 199',
      '--cm-primary-dim':               '146 64 14',
      '--cm-on-primary':                '255 255 255',
      '--cm-on-primary-container':      '120 53 15',
      '--cm-secondary':                 '101 163 13',
      '--cm-secondary-container':       '217 249 157',
      '--cm-on-secondary-container':    '63 98 18',
      '--cm-surface':                   '250 250 249',
      '--cm-surface-dim':               '231 229 228',
      '--cm-surface-bright':            '250 250 249',
      '--cm-surface-container':         '245 245 244',
      '--cm-surface-container-low':     '250 250 249',
      '--cm-surface-container-high':    '231 229 228',
      '--cm-surface-container-highest': '214 211 209',
      '--cm-surface-container-lowest':  '255 255 255',
      '--cm-surface-variant':           '231 229 228',
      '--cm-on-surface':                '28 25 23',
      '--cm-on-surface-variant':        '87 83 78',
      '--cm-on-background':             '28 25 23',
      '--cm-background':                '250 250 249',
      '--cm-outline':                   '168 162 158',
      '--cm-outline-variant':           '214 211 209',
    },
    chrome: {
      '--chrome-bg':             '#fafaf9',
      '--chrome-text':           '#1c1917',
      '--chrome-sidebar-bg':     '#ffffff',
      '--chrome-right-sidebar':  'rgba(245,245,244,0.5)',
      '--chrome-strong':         '#1c1917',
      '--chrome-muted':          'rgba(87,83,78,0.7)',
      '--chrome-label':          'rgba(68,64,60,0.6)',
      '--chrome-faint':          'rgba(87,83,78,0.6)',
      '--chrome-body':           'rgba(28,25,23,0.8)',
      '--chrome-active-bg':      'rgba(254,243,199,0.5)',
      '--chrome-active-border':  '#b45309',
      '--chrome-hover-bg':       '#f5f5f4',
      '--chrome-border-subtle':  'rgba(28,25,23,0.05)',
      '--chrome-border-divider': 'rgba(28,25,23,0.08)',
      '--chrome-avatar':         '#b45309',
      '--chrome-avatar-accent':  '#d97706',
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════════
     CLEAN  –  white surfaces, blue accent, gray sidebars  (modern SaaS)
     Principle: color is ONLY used to indicate state. Surfaces are neutral.
     ═══════════════════════════════════════════════════════════════════════════ */
  clean: {
    name: 'Clean',
    ds: {
      '--cm-primary':                   '37 99 235',      // blue-600 – main accent
      '--cm-primary-container':         '96 165 250',     // blue-400 – smooth gradient endpoint
      '--cm-primary-fixed':             '191 219 254',    // blue-200 – subtle tints
      '--cm-primary-dim':               '29 78 216',      // blue-700
      '--cm-on-primary':                '255 255 255',
      '--cm-on-primary-container':      '30 58 138',
      '--cm-secondary':                 '2 132 199',      // sky-600 – same blue family
      '--cm-secondary-container':       '186 230 253',    // sky-200
      '--cm-on-secondary-container':    '12 74 110',      // sky-900
      '--cm-surface':                   '255 255 255',    // pure white content
      '--cm-surface-dim':               '237 238 242',
      '--cm-surface-bright':            '255 255 255',
      '--cm-surface-container':         '241 243 245',    // cards
      '--cm-surface-container-low':     '249 250 251',
      '--cm-surface-container-high':    '232 234 238',    // skeletons / hover (darker)
      '--cm-surface-container-highest': '241 243 245',    // search bar = same as cards
      '--cm-surface-container-lowest':  '255 255 255',
      '--cm-surface-variant':           '243 244 246',
      '--cm-on-surface':                '17 24 39',       // gray-900
      '--cm-on-surface-variant':        '107 114 128',    // gray-500
      '--cm-on-background':             '17 24 39',
      '--cm-background':                '255 255 255',
      '--cm-outline':                   '209 213 219',    // gray-300
      '--cm-outline-variant':           '229 231 235',    // gray-200
    },
    chrome: {
      '--chrome-bg':             '#ffffff',
      '--chrome-text':           '#111827',
      '--chrome-sidebar-bg':     '#f9fafb',
      '--chrome-right-sidebar':  'rgba(249,250,251,0.8)',
      '--chrome-strong':         '#111827',
      '--chrome-muted':          'rgba(107,114,128,0.8)',
      '--chrome-label':          'rgba(75,85,99,0.6)',
      '--chrome-faint':          'rgba(107,114,128,0.5)',
      '--chrome-body':           'rgba(17,24,39,0.75)',
      '--chrome-active-bg':      'rgba(239,246,255,0.7)',
      '--chrome-active-border':  '#2563eb',
      '--chrome-hover-bg':       '#f3f4f6',
      '--chrome-border-subtle':  'rgba(0,0,0,0.04)',
      '--chrome-border-divider': 'rgba(0,0,0,0.08)',
      '--chrome-avatar':         '#2563eb',
      '--chrome-avatar-accent':  '#3b82f6',
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════════
     MINIMAL  –  warm slate tones, functional, understated
     Principle: one color family (slate), calm & readable.
     ═══════════════════════════════════════════════════════════════════════════ */
  minimal: {
    name: 'Minimal',
    ds: {
      '--cm-primary':                   '51 65 85',       // slate-700 – firm but not harsh
      '--cm-primary-container':         '100 116 139',    // slate-500 – warm gradient endpoint
      '--cm-primary-fixed':             '226 232 240',    // slate-200
      '--cm-primary-dim':               '30 41 59',       // slate-800
      '--cm-on-primary':                '255 255 255',
      '--cm-on-primary-container':      '30 41 59',
      '--cm-secondary':                 '71 85 105',      // slate-600 – same family
      '--cm-secondary-container':       '226 232 240',    // slate-200
      '--cm-on-secondary-container':    '30 41 59',
      '--cm-surface':                   '255 255 255',
      '--cm-surface-dim':               '241 245 249',    // slate-100
      '--cm-surface-bright':            '255 255 255',
      '--cm-surface-container':         '248 250 252',    // slate-50 – cards
      '--cm-surface-container-low':     '255 255 255',
      '--cm-surface-container-high':    '241 245 249',    // slate-100
      '--cm-surface-container-highest': '248 250 252',    // search bar = same as cards
      '--cm-surface-container-lowest':  '255 255 255',
      '--cm-surface-variant':           '241 245 249',
      '--cm-on-surface':                '15 23 42',       // slate-900
      '--cm-on-surface-variant':        '100 116 139',    // slate-500
      '--cm-on-background':             '15 23 42',
      '--cm-background':                '255 255 255',
      '--cm-outline':                   '203 213 225',    // slate-300
      '--cm-outline-variant':           '226 232 240',    // slate-200
    },
    chrome: {
      '--chrome-bg':             '#f8fafc',
      '--chrome-text':           '#0f172a',
      '--chrome-sidebar-bg':     '#ffffff',
      '--chrome-right-sidebar':  'rgba(248,250,252,0.8)',
      '--chrome-strong':         '#0f172a',
      '--chrome-muted':          'rgba(71,85,105,0.7)',
      '--chrome-label':          'rgba(71,85,105,0.55)',
      '--chrome-faint':          'rgba(100,116,139,0.5)',
      '--chrome-body':           'rgba(15,23,42,0.75)',
      '--chrome-active-bg':      'rgba(241,245,249,0.7)',
      '--chrome-active-border':  '#334155',
      '--chrome-hover-bg':       '#f1f5f9',
      '--chrome-border-subtle':  'rgba(0,0,0,0.04)',
      '--chrome-border-divider': 'rgba(0,0,0,0.07)',
      '--chrome-avatar':         '#475569',
      '--chrome-avatar-accent':  '#64748b',
    },
  },
  /* ═══════════════════════════════════════════════════════════════════════════
     FORUM  –  Adidas Forum Low colorway
     Green stripes · stone leather · cream midsole · gum outsole · gold thread
     ═══════════════════════════════════════════════════════════════════════════ */
  forum: {
    name: 'Forum',
    ds: {
      '--cm-primary':                   '53 148 79',       // #35944F – stripe green
      '--cm-primary-container':         '142 200 160',     // #8EC8A0 – soft green (gradient endpoint)
      '--cm-primary-fixed':             '203 232 211',     // #CBE8D3 – lightest green tint
      '--cm-primary-dim':               '38 125 62',       // #267D3E – darker green
      '--cm-on-primary':                '255 255 255',
      '--cm-on-primary-container':      '28 100 48',       // #1C6430 – deep green
      '--cm-secondary':                 '190 163 112',     // #BEA370 – gold embroidery
      '--cm-secondary-container':       '237 226 200',     // #EDE2C8 – light gold
      '--cm-on-secondary-container':    '120 95 48',       // #785F30 – dark gold
      '--cm-surface':                   '244 238 228',     // #F4EEE4 – cream (midsole)
      '--cm-surface-dim':               '218 210 198',     // #DAD2C6 – stone mid
      '--cm-surface-bright':            '244 238 228',     // #F4EEE4
      '--cm-surface-container':         '232 226 216',     // #E8E2D8 – stone leather
      '--cm-surface-container-low':     '240 234 224',     // #F0EAE0 – light cream
      '--cm-surface-container-high':    '218 210 198',     // #DAD2C6 – darker stone
      '--cm-surface-container-highest': '232 226 216',     // #E8E2D8 – search bar = cards
      '--cm-surface-container-lowest':  '250 246 240',     // #FAF6F0 – near white warm
      '--cm-surface-variant':           '224 217 207',     // #E0D9CF – stone variant
      '--cm-on-surface':                '58 50 38',        // #3A3226 – dark warm brown
      '--cm-on-surface-variant':        '120 110 95',      // #786E5F – muted brown
      '--cm-on-background':             '58 50 38',        // #3A3226
      '--cm-background':                '244 238 228',     // #F4EEE4
      '--cm-outline':                   '180 172 158',     // #B4AC9E – warm gray
      '--cm-outline-variant':           '208 200 188',     // #D0C8BC – light stone border
    },
    chrome: {
      '--chrome-bg':             '#F4EEE4',                // cream midsole
      '--chrome-text':           '#3A3226',                // dark warm brown
      '--chrome-sidebar-bg':     '#FAF6F0',                // warm near-white
      '--chrome-right-sidebar':  'rgba(240,234,224,0.5)',  // light cream
      '--chrome-strong':         '#3A3226',                // dark brown
      '--chrome-muted':          'rgba(120,110,95,0.75)',  // muted stone
      '--chrome-label':          'rgba(100,90,72,0.6)',    // warm label
      '--chrome-faint':          'rgba(120,110,95,0.55)',  // faint stone
      '--chrome-body':           'rgba(58,50,38,0.8)',     // body text
      '--chrome-active-bg':      'rgba(203,232,211,0.4)',  // green tint bg
      '--chrome-active-border':  '#35944F',                // stripe green
      '--chrome-hover-bg':       '#EDE7DB',                // cream hover
      '--chrome-border-subtle':  'rgba(58,50,38,0.05)',    // subtle warm
      '--chrome-border-divider': 'rgba(58,50,38,0.1)',     // divider warm
      '--chrome-avatar':         '#35944F',                // stripe green
      '--chrome-avatar-accent':  '#C49D65',                // gum sole
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════════
     PRO-NEUTRAL  –  Amazon-style blue/white professional theme
     ═══════════════════════════════════════════════════════════════════════════ */
  'pro-neutral': {
    name: 'Pro Neutral',
    cardShadow: true,
    ds: {
      '--cm-primary':                   '37 99 235',
      '--cm-primary-container':         '59 130 246',
      '--cm-primary-fixed':             '219 234 254',
      '--cm-primary-dim':               '29 78 216',
      '--cm-on-primary':                '255 255 255',
      '--cm-on-primary-container':      '255 255 255',
      '--cm-secondary':                 '5 150 105',
      '--cm-secondary-container':       '209 250 229',
      '--cm-on-secondary-container':    '4 120 87',
      '--cm-surface':                   '255 255 255',
      '--cm-surface-dim':               '229 231 235',
      '--cm-surface-bright':            '255 255 255',
      '--cm-surface-container':         '243 244 246',
      '--cm-surface-container-low':     '249 250 251',
      '--cm-surface-container-high':    '229 231 235',
      '--cm-surface-container-highest': '209 213 219',
      '--cm-surface-container-lowest':  '255 255 255',
      '--cm-surface-variant':           '229 231 235',
      '--cm-on-surface':                '17 24 39',
      '--cm-on-surface-variant':        '75 85 99',
      '--cm-on-background':             '17 24 39',
      '--cm-background':                '249 250 251',
      '--cm-outline':                   '209 213 219',
      '--cm-outline-variant':           '229 231 235',
    },
    chrome: {
      '--chrome-bg':              '#f9fafb',
      '--chrome-sidebar-bg':      '#ffffff',
      '--chrome-text':            '#111827',
      '--chrome-strong':          '#111827',
      '--chrome-muted':           '#4b5563',
      '--chrome-faint':           '#9ca3af',
      '--chrome-active-bg':       '#eff6ff',
      '--chrome-active-border':   '#2563eb',
      '--chrome-hover-bg':        '#f3f4f6',
      '--chrome-border-subtle':   'rgba(0,0,0,0.06)',
      '--chrome-border-divider':  'rgba(0,0,0,0.08)',
      '--chrome-avatar':          '#2563eb',
      '--chrome-avatar-accent':   '#2563eb',
      '--chrome-right-sidebar':   'rgba(248,250,252,0.8)',
      '--chrome-label':           'rgba(75,85,99,0.6)',
      '--chrome-body':            'rgba(17,24,39,0.75)',
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════════
     PRO-WARM  –  Mercado Libre-style yellow/warm theme
     ═══════════════════════════════════════════════════════════════════════════ */
  'pro-warm': {
    name: 'Pro Warm',
    cardShadow: true,
    ds: {
      '--cm-primary':                   '234 179 8',
      '--cm-primary-container':         '253 224 71',
      '--cm-primary-fixed':             '254 252 232',
      '--cm-primary-dim':               '202 138 4',
      '--cm-on-primary':                '15 23 42',
      '--cm-on-primary-container':      '15 23 42',
      '--cm-secondary':                 '37 99 235',
      '--cm-secondary-container':       '219 234 254',
      '--cm-on-secondary-container':    '29 78 216',
      '--cm-surface':                   '255 255 255',
      '--cm-surface-dim':               '229 231 235',
      '--cm-surface-bright':            '255 255 255',
      '--cm-surface-container':         '254 252 232',
      '--cm-surface-container-low':     '255 255 255',
      '--cm-surface-container-high':    '254 249 195',
      '--cm-surface-container-highest': '253 224 71',
      '--cm-surface-container-lowest':  '255 255 255',
      '--cm-surface-variant':           '254 249 195',
      '--cm-on-surface':                '15 23 42',
      '--cm-on-surface-variant':        '75 85 99',
      '--cm-on-background':             '15 23 42',
      '--cm-background':                '255 255 255',
      '--cm-outline':                   '209 213 219',
      '--cm-outline-variant':           '229 231 235',
    },
    chrome: {
      '--chrome-bg':              '#ffffff',
      '--chrome-sidebar-bg':      '#fffde7',
      '--chrome-text':            '#0f172a',
      '--chrome-strong':          '#0f172a',
      '--chrome-muted':           '#4b5563',
      '--chrome-faint':           '#9ca3af',
      '--chrome-active-bg':       '#fef9c3',
      '--chrome-active-border':   '#eab308',
      '--chrome-hover-bg':        '#fef9c3',
      '--chrome-border-subtle':   'rgba(0,0,0,0.06)',
      '--chrome-border-divider':  'rgba(0,0,0,0.08)',
      '--chrome-avatar':          '#eab308',
      '--chrome-avatar-accent':   '#2563eb',
      '--chrome-right-sidebar':   'rgba(254,252,232,0.7)',
      '--chrome-label':           'rgba(75,85,99,0.6)',
      '--chrome-body':            'rgba(15,23,42,0.75)',
    },
  },
};

/* ═══════════════════════════════════════════════════════════════════════════════
   ► CHANGE THIS LINE TO SWITCH THEMES ◄
   Options: 'emerald' | 'emerald-light' | 'emerald-cards' | 'ocean' | 'violet' | 'warm' | 'slate' | 'rose' | 'sand' | 'clean' | 'minimal' | 'forum' | 'pro-neutral' | 'pro-warm'
   ═══════════════════════════════════════════════════════════════════════════════ */
export const ACTIVE_THEME = 'pro-neutral';

/** Merge design-system + chrome vars into a single style object. */
export function getThemeVars(themeName = ACTIVE_THEME) {
  const theme = THEMES[themeName] || THEMES.emerald;
  return { ...theme.ds, ...theme.chrome };
}

/** Whether the active theme wants elevated (shadowed) product cards. */
export function hasCardShadow(themeName = ACTIVE_THEME) {
  return !!(THEMES[themeName] || THEMES.emerald).cardShadow;
}

export default THEMES;
